import React, { useState, useEffect } from 'react'
import { getVendors, getMenuItemStatistics } from '@/api/foodOrders'
import { BarChart3, TrendingUp, Calendar, DollarSign, Package, ChevronDown, ChevronUp } from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

interface Vendor {
  id: string
  vendor_name: string
  vendor_type: string
  is_active: boolean
}

interface MenuItemStats {
  item_id: string
  item_name: string
  category: string
  total_orders: number
  last_ordered: string | null
  total_revenue: number
  avg_price: number
  popularity_rank: number
}

type SortField = 'item_name' | 'total_orders' | 'total_revenue' | 'last_ordered'
type SortDirection = 'asc' | 'desc'

export function ParentMenuStatistics() {
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [selectedVendorId, setSelectedVendorId] = useState<string>('')
  const [statistics, setStatistics] = useState<MenuItemStats[]>([])
  const [loading, setLoading] = useState(false)
  const [sortField, setSortField] = useState<SortField>('total_orders')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadVendors()
  }, [])

  useEffect(() => {
    if (selectedVendorId) {
      loadStatistics()
    }
  }, [selectedVendorId])

  const loadVendors = async () => {
    try {
      const data = await getVendors(false) // Get all vendors, not just active
      setVendors(data || [])
      if (data && data.length > 0) {
        setSelectedVendorId(data[0].id)
      }
    } catch (error) {
      console.error('Failed to load vendors:', error)
      toast.error('Failed to load vendors')
    }
  }

  const loadStatistics = async () => {
    if (!selectedVendorId) return

    try {
      setLoading(true)
      const data = await getMenuItemStatistics(selectedVendorId)
      setStatistics(data || [])
    } catch (error) {
      console.error('Failed to load statistics:', error)
      toast.error('Failed to load menu statistics')
    } finally {
      setLoading(false)
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories)
    if (newExpanded.has(category)) {
      newExpanded.delete(category)
    } else {
      newExpanded.add(category)
    }
    setExpandedCategories(newExpanded)
  }

  const sortedStatistics = [...statistics].sort((a, b) => {
    let aVal: any = a[sortField]
    let bVal: any = b[sortField]

    if (sortField === 'last_ordered') {
      aVal = aVal ? new Date(aVal).getTime() : 0
      bVal = bVal ? new Date(bVal).getTime() : 0
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  // Group by category
  const statsByCategory = sortedStatistics.reduce((acc, stat) => {
    if (!acc[stat.category]) {
      acc[stat.category] = []
    }
    acc[stat.category].push(stat)
    return acc
  }, {} as Record<string, MenuItemStats[]>)

  const totalOrders = statistics.reduce((sum, stat) => sum + stat.total_orders, 0)
  const totalRevenue = statistics.reduce((sum, stat) => sum + stat.total_revenue, 0)
  const uniqueItems = statistics.length

  const selectedVendor = vendors.find(v => v.id === selectedVendorId)

  const SortButton: React.FC<{ field: SortField; label: string }> = ({ field, label }) => (
    <button
      onClick={() => handleSort(field)}
      className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
        sortField === field
          ? 'bg-blue-100 text-blue-700'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
      {sortField === field && (
        sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
      )}
    </button>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-7 h-7" />
          Menu Item Statistics
        </h2>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
          <select
            value={selectedVendorId}
            onChange={(e) => setSelectedVendorId(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
          >
            {vendors.map(vendor => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.vendor_name} {!vendor.is_active && '(Inactive)'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading statistics...</p>
        </div>
      ) : statistics.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No order data available for this vendor</p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium opacity-90">Total Orders</h3>
                <TrendingUp className="w-5 h-5 opacity-75" />
              </div>
              <p className="text-3xl font-bold">{totalOrders.toLocaleString()}</p>
              <p className="text-xs opacity-75 mt-1">{uniqueItems} unique items</p>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium opacity-90">Total Revenue</h3>
                <DollarSign className="w-5 h-5 opacity-75" />
              </div>
              <p className="text-3xl font-bold">${totalRevenue.toFixed(2)}</p>
              <p className="text-xs opacity-75 mt-1">
                Avg ${totalOrders > 0 ? (totalRevenue / totalOrders).toFixed(2) : '0.00'} per order
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium opacity-90">Top Seller</h3>
                <Package className="w-5 h-5 opacity-75" />
              </div>
              {sortedStatistics.length > 0 && (
                <>
                  <p className="text-lg font-bold truncate">{sortedStatistics[0].item_name}</p>
                  <p className="text-xs opacity-75 mt-1">
                    {sortedStatistics[0].total_orders} orders, ${sortedStatistics[0].total_revenue.toFixed(2)}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Sort Controls */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700">Sort by:</span>
            <SortButton field="item_name" label="Name" />
            <SortButton field="total_orders" label="Orders" />
            <SortButton field="total_revenue" label="Revenue" />
            <SortButton field="last_ordered" label="Last Ordered" />
          </div>

          {/* Statistics by Category */}
          <div className="space-y-4">
            {Object.entries(statsByCategory).map(([category, items]) => {
              const isExpanded = expandedCategories.has(category)
              const categoryOrders = items.reduce((sum, item) => sum + item.total_orders, 0)
              const categoryRevenue = items.reduce((sum, item) => sum + item.total_revenue, 0)

              return (
                <div key={category} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div
                    className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => toggleCategory(category)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-gray-900">{category}</h3>
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          {items.length} items
                        </span>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-sm text-gray-600">{categoryOrders} orders</p>
                          <p className="text-sm font-semibold text-gray-900">${categoryRevenue.toFixed(2)}</p>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="p-4">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Item Name
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Orders
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Revenue
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Avg Price
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Last Ordered
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Rank
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {items.map((stat) => (
                              <tr key={stat.item_id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  {stat.item_name}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">
                                  {stat.total_orders}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">
                                  ${stat.total_revenue.toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 text-right">
                                  ${stat.avg_price.toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 text-right">
                                  {stat.last_ordered ? (
                                    <span className="flex items-center justify-end gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {format(new Date(stat.last_ordered), 'MMM d, yyyy')}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">Never</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                                    stat.popularity_rank === 1
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : stat.popularity_rank <= 3
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {stat.popularity_rank}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
